import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, Copy, Download, Clock, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { DigestSchedule } from "@/components/DigestSchedule";

const WINDOWS = [
  { label: "24h", hours: 24 },
  { label: "7d", hours: 24 * 7 },
  { label: "30d", hours: 24 * 30 },
];

export default function SignalDigest({ open, onOpenChange }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState(24);

  const load = async (h) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/signals/digest?hours=${h}`);
      setRecap(data);
    } catch {
      toast.error("Could not build digest");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load(hours);
    // eslint-disable-next-line
  }, [open, hours]);

  const copyHtml = async () => {
    if (!recap?.html) return;
    await navigator.clipboard.writeText(recap.html);
    toast.success("HTML copied — paste into Gmail, Superhuman, or your CRM");
  };

  const downloadHtml = () => {
    if (!recap?.html) return;
    const blob = new Blob([recap.html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `aipipe-signal-digest-${new Date().toISOString().slice(0, 10)}.html`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl bg-[#0B1020] border-white/10 text-slate-100 p-0 overflow-hidden max-h-[90vh] flex flex-col"
        data-testid="signal-digest-dialog"
      >
        <DialogHeader className="p-6 pb-4 border-b border-white/5">
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-emerald-300" />
            Signal digest preview
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            The exact email you'll receive. Schedule a daily send, or send one now.
          </DialogDescription>
        </DialogHeader>

        <DigestSchedule />

        <div className="px-6 py-3 border-b border-white/5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1 text-xs">
            <Clock className="w-3.5 h-3.5 text-slate-500 mr-1" />
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                onClick={() => setHours(w.hours)}
                data-testid={`digest-window-${w.label}`}
                className={`h-7 px-3 rounded-full font-mono transition ${
                  hours === w.hours
                    ? "bg-emerald-500 text-slate-950 font-semibold"
                    : "bg-white/[0.03] border border-white/10 text-slate-400 hover:text-white"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => load(hours)} disabled={loading} data-testid="digest-refresh-btn" className="h-8 border-white/10 bg-white/[0.02]">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" variant="outline" onClick={copyHtml} disabled={!recap?.html} data-testid="digest-copy-btn" className="h-8 border-white/10 bg-white/[0.02]">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy HTML
            </Button>
            <Button size="sm" onClick={downloadHtml} disabled={!recap?.html} data-testid="digest-download-btn" className="h-8 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
          </div>
        </div>

        {recap && (
          <div className="px-6 py-3 border-b border-white/5 grid grid-cols-4 gap-3 text-center">
            {[
              ["Fired", recap.stats.fired, "text-white"],
              ["Red hot", recap.stats.red_hot, "text-rose-300"],
              ["Auto-sent", recap.stats.auto_sent, "text-emerald-300"],
              ["Awaiting", recap.stats.awaiting, "text-sky-300"],
            ].map(([label, v, cls]) => (
              <div key={label} className="rounded-lg bg-white/[0.02] border border-white/5 p-2">
                <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500">{label}</div>
                <div className={`text-lg font-bold ${cls}`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-[#0A0D14]" data-testid="digest-preview-frame">
          {loading || !recap ? (
            <div className="h-96 grid place-items-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <iframe
              title="Digest email preview"
              srcDoc={recap.html}
              className="w-full min-h-[600px] border-0"
              sandbox="allow-same-origin"
            />
          )}
        </div>

        <div className="px-6 py-3 border-t border-white/5 flex items-center justify-between text-xs text-slate-500">
          <div className="font-mono">
            {recap?.window_label} · {recap?.stats?.fired ?? 0} items
          </div>
          <span>Delivered by email{" "}&middot; Slack too if connected</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
